import React, { useState, useEffect } from 'react';
import { Formik, Form } from 'formik';
import * as Yup from 'yup';
import InformationsPersonnelles from './steps/InformationsPersonnelles';
import InformationsApicoles from './steps/InformationsApicoles';
import Recapitulatif from './steps/Recapitulatif';
import Authentication from './steps/Authentication';
import InformationsAMAIR from './steps/InformationsAMAIR';
import AdhesionAMAIRGratuite from './steps/AdhesionAMAIRGratuite';
import Paiement from './steps/Paiement';
import { useAuth } from '../../context/AuthContext';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';

const getSteps = (user, organisme, hasActiveAMAIRAdhesion = false) => [
  'Informations personnelles',
  'Informations apicoles',
  ...(organisme === 'AMAIR' ? ['Informations AMAIR'] : []),
  ...(organisme === 'SAR' && !hasActiveAMAIRAdhesion ? ['Adhésion à l\'AMAIR'] : []),
  'Paiement',
  ...(!user ? ['Création de compte'] : []),
  'Récapitulatif'
];

const getInitialValuesForOrganisme = (organisme, annee) => ({
  organisme: organisme, // Organisme unique défini par l'URL
  annee: annee || new Date().getFullYear(), // Année passée en prop
  informationsPersonnelles: {
    typePersonne: 'personne_physique',
    designation: '',
    raisonSociale: '',
    nom: '',
    prenom: '',
    dateNaissance: null,
    adresse: {
      rue: '',
      codePostal: '',
      ville: ''
    },
    telephone: '',
    telephoneMobile: '',
    email: ''
  },
  informationsApicoles: {
    nombreRuches: '',
    nombreRuchers: '',
    localisation: {
      departement: '',
      commune: ''
    },
    napi: '',
    numeroAmexa: '',
    siret: ''
  },
  informationsSpecifiques: {
    SAR: {},
    AMAIR: {}
  },
  adhesionAMAIRGratuite: false,
  documentsCommuns: [],
  documentsSAR: [],
  documentsAMAIR: [],
  paiement: {
    typePaiement: ''
  },
  auth: {
    password: '',
    confirmPassword: ''
  },
  acceptStatuts: false,
  acceptReglementSAR: false,
  acceptStatutsAMAIR: false,
  acceptReglementAMAIR: false,
  acceptConditions: false
});

const validationSchema = (user, organisme) => Yup.object().shape({
  annee: Yup.number()
    .required('Veuillez sélectionner une année')
    .min(new Date().getFullYear(), 'L\'année doit être l\'année en cours ou future'),
  informationsPersonnelles: Yup.object().shape({
    designation: Yup.string().required('La civilité est requise'),
    nom: Yup.string().required('Le nom est requis'),
    prenom: Yup.string().required('Le prénom est requis'),
    dateNaissance: Yup.date().required('La date de naissance est requise'),
    adresse: Yup.object().shape({
      rue: Yup.string().required('L\'adresse est requise'),
      codePostal: Yup.string().required('Le code postal est requis'),
      ville: Yup.string().required('La ville est requise')
    }),
    telephone: Yup.string(),
    telephoneMobile: Yup.string().required('Le téléphone mobile est requis'),
    email: Yup.string().email('Email invalide').required('L\'email est requis')
  }),
  informationsApicoles: Yup.object().shape({
    nombreRuches: Yup.number().required('Le nombre de ruches est requis'),
    nombreRuchers: Yup.number().required('Le nombre d\'emplacements est requis'),
    localisation: Yup.object().shape({
      departement: Yup.string().required('Le département est requis'),
      commune: Yup.string().required('La commune est requise')
    }),
    napi: Yup.string(),
    siret: Yup.string().matches(/^\d{14}$/, 'Le SIRET doit contenir exactement 14 chiffres', { excludeEmptyString: true })
  }),
  paiement: Yup.object().when('informationsSpecifiques', {
    is: (informationsSpecifiques) => organisme === 'AMAIR' && informationsSpecifiques?.AMAIR?.adherentSAR === true,
    then: (schema) => schema.shape({
      typePaiement: Yup.string().notRequired()
    }),
    otherwise: (schema) => schema.shape({
      typePaiement: Yup.string()
        .required('Veuillez sélectionner un mode de paiement')
        .oneOf(['cheque', 'en_ligne'], 'Mode de paiement invalide')
    })
  }),
  auth: user ? Yup.object() : Yup.object().shape({
    password: Yup.string()
      .required('Le mot de passe est requis')
      .min(8, 'Le mot de passe doit contenir au moins 8 caractères')
      .matches(/[A-Z]/, 'Le mot de passe doit contenir au moins une majuscule')
      .matches(/[a-z]/, 'Le mot de passe doit contenir au moins une minuscule')
      .matches(/[0-9]/, 'Le mot de passe doit contenir au moins un chiffre')
      .matches(/[!@#$%^&*(),.?":{}|<>_\-+=[\]\\;'/`~]/, 'Le mot de passe doit contenir au moins un caractère spécial'),
    confirmPassword: Yup.string()
      .required('La confirmation du mot de passe est requise')
      .oneOf([Yup.ref('password')], 'Les mots de passe ne correspondent pas')
  }),
  acceptStatuts: organisme === 'SAR' 
    ? Yup.boolean()
        .oneOf([true], 'Vous devez accepter les statuts du SAR')
        .required('Vous devez accepter les statuts du SAR')
    : Yup.boolean(),
  acceptReglementSAR: organisme === 'SAR'
    ? Yup.boolean()
        .oneOf([true], 'Vous devez accepter le règlement intérieur du SAR')
        .required('Vous devez accepter le règlement intérieur du SAR')
    : Yup.boolean(),
  acceptStatutsAMAIR: Yup.boolean().when('adhesionAMAIRGratuite', {
    is: true,
    then: (schema) => schema.oneOf([true], 'Vous devez accepter les statuts de l\'AMAIR').required(),
    otherwise: (schema) => organisme === 'AMAIR' 
      ? schema.oneOf([true], 'Vous devez accepter les statuts de l\'AMAIR').required()
      : schema
  }),
  acceptReglementAMAIR: Yup.boolean().when('adhesionAMAIRGratuite', {
    is: true,
    then: (schema) => schema.oneOf([true], 'Vous devez accepter le règlement intérieur de l\'AMAIR').required(),
    otherwise: (schema) => organisme === 'AMAIR'
      ? schema.oneOf([true], 'Vous devez accepter le règlement intérieur de l\'AMAIR').required()
      : schema
  }),
  acceptConditions: Yup.boolean()
    .oneOf([true], 'Vous devez accepter les conditions générales du site')
    .required('Vous devez accepter les conditions générales du site'),
  signature: Yup.string().required('La signature numérique est obligatoire')
});

export default function AdhesionFormDedicated({ organisme, annee }) {
  const [activeStep, setActiveStep] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [stepError, setStepError] = useState('');
  const [userPersonalInfo, setUserPersonalInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [emailExistsError, setEmailExistsError] = useState(false);
  const [sarMembershipStatus, setSarMembershipStatus] = useState(null); // null, 'actif', 'en_attente', 'paiement_demande'
  const [amairMembershipStatus, setAmairMembershipStatus] = useState(null); // null, 'actif', 'en_attente', 'paiement_demande'
  const { user, login } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (user) {
      fetchUserData();
    } else {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    // Vérifier si l'utilisateur a une adhésion SAR ou AMAIR active ou en attente pour la MÊME année
    const checkMemberships = async () => {
      if (user && annee) {
        try {
          const token = localStorage.getItem('token');
          const response = await axios.get(`${process.env.REACT_APP_API_URL}/adhesions/my-adhesions`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          
          // Vérifier si l'utilisateur a une adhésion SAR pour la même année (pour formulaire AMAIR)
          if (organisme === 'AMAIR') {
            const sarAdhesion = response.data.find(
              adhesion => adhesion.organisme === 'SAR' 
                && ['actif', 'en_attente', 'paiement_demande'].includes(adhesion.status)
                && adhesion.annee === annee
            );
            setSarMembershipStatus(sarAdhesion ? sarAdhesion.status : null);
          }
          
          // Vérifier si l'utilisateur a une adhésion AMAIR pour la même année (pour formulaire SAR)
          if (organisme === 'SAR') {
            const amairAdhesion = response.data.find(
              adhesion => adhesion.organisme === 'AMAIR' 
                && ['actif'].includes(adhesion.status)
                && adhesion.annee === annee
            );
            setAmairMembershipStatus(amairAdhesion ? amairAdhesion.status : null);
          }
        } catch (error) {
          console.error('Erreur lors de la vérification des adhésions:', error);
          setSarMembershipStatus(null);
          setAmairMembershipStatus(null);
        }
      } else {
        setSarMembershipStatus(null);
        setAmairMembershipStatus(null);
      }
    };

    checkMemberships();
  }, [user, organisme, annee]);

  const fetchUserData = async () => {
    try {
      const token = localStorage.getItem('token');
      
      // Récupérer les infos personnelles
      const profileResponse = await axios.get(`${process.env.REACT_APP_API_URL}/auth/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Combiner les données de base de l'utilisateur avec personalInfo
      const combinedInfo = {
        typePersonne: profileResponse.data.personalInfo?.typePersonne || profileResponse.data.typePersonne || profileResponse.data.type || '',
        raisonSociale: profileResponse.data.personalInfo?.raisonSociale || profileResponse.data.raisonSociale || '',
        designation: profileResponse.data.personalInfo?.designation || profileResponse.data.designation || '',
        nom: profileResponse.data.personalInfo?.nom || profileResponse.data.nom || '',
        prenom: profileResponse.data.personalInfo?.prenom || profileResponse.data.prenom || '',
        email: profileResponse.data.personalInfo?.email || profileResponse.data.email || '',
        telephone: profileResponse.data.personalInfo?.telephone || profileResponse.data.telephone || '',
        telephoneMobile: profileResponse.data.personalInfo?.telephoneMobile || profileResponse.data.telephoneMobile || '',
        dateNaissance: profileResponse.data.personalInfo?.dateNaissance || profileResponse.data.dateNaissance || null,
        adresse: profileResponse.data.personalInfo?.adresse || profileResponse.data.adresse || { rue: '', codePostal: '', ville: '' },
        // Informations apicoles
        napi: profileResponse.data.napi || '',
        numeroAmexa: profileResponse.data.numeroAmexa || '',
        siret: profileResponse.data.siret || ''
      };
      
      setUserPersonalInfo(combinedInfo);
    } catch (error) {
      console.error('Erreur lors du chargement des données utilisateur:', error);
    } finally {
      setLoading(false);
    }
  };

  const validateCurrentStep = async (formikProps, stepName) => {
    const { setTouched, validateForm, values } = formikProps;
    
    // Définir les champs à valider pour chaque étape
    const stepFieldsMap = {
      'Informations personnelles': [
        'informationsPersonnelles.designation',
        'informationsPersonnelles.nom',
        'informationsPersonnelles.prenom',
        'informationsPersonnelles.dateNaissance',
        'informationsPersonnelles.adresse.rue',
        'informationsPersonnelles.adresse.codePostal',
        'informationsPersonnelles.adresse.ville',
        'informationsPersonnelles.telephone',
        'informationsPersonnelles.email'
      ],
      'Informations apicoles': [
        'informationsApicoles.nombreRuches',
        'informationsApicoles.nombreRuchers',
        'informationsApicoles.localisation.departement',
        'informationsApicoles.localisation.commune'
      ],
      'Paiement': ['paiement.typePaiement'],
      'Création de compte': ['auth.password', 'auth.confirmPassword'],
      'Récapitulatif': ['acceptConditions', 'signature']
    };

    // Ajouter acceptStatuts si SAR
    if (values.organisme === 'SAR') {
      stepFieldsMap['Récapitulatif'].push('acceptStatuts', 'acceptReglementSAR');
    }

    if (values.organisme === 'AMAIR' || (values.organisme === 'SAR' && values.adhesionAMAIRGratuite)) {
      stepFieldsMap['Récapitulatif'].push('acceptStatutsAMAIR', 'acceptReglementAMAIR');
    }

    // Validation spécifique pour Informations apicoles (inclut les documents)
    if (stepName === 'Informations apicoles') {
      const documentsField = `documents${organisme}`;
      const documents = values[documentsField] || [];
      const hasDeclarationRuches = documents.some(doc => doc.type === 'declaration_ruches');
      
      if (!hasDeclarationRuches) {
        setStepError('Veuillez télécharger votre déclaration de ruches pour continuer');
        window.scrollTo({ top: 0, behavior: 'smooth' });
        return false;
      }
    }

    // Valider tous les champs du formulaire
    const errors = await validateForm();
    
    // Récupérer les champs de l'étape actuelle
    const fieldsToValidate = stepFieldsMap[stepName] || [];
    
    // Marquer les champs de l'étape comme "touchés"
    const touchedFields = {};
    fieldsToValidate.forEach(field => {
      const parts = field.split('.');
      let current = touchedFields;
      parts.forEach((part, index) => {
        if (index === parts.length - 1) {
          current[part] = true;
        } else {
          current[part] = current[part] || {};
          current = current[part];
        }
      });
    });
    setTouched(touchedFields);

    // Vérifier s'il y a des erreurs pour les champs de cette étape
    let hasErrors = false;
    fieldsToValidate.forEach(field => {
      const parts = field.split('.');
      let errorValue = errors;
      for (const part of parts) {
        if (errorValue && errorValue[part] !== undefined) {
          errorValue = errorValue[part];
        } else {
          errorValue = null;
          break;
        }
      }
      if (errorValue) {
        hasErrors = true;
      }
    });

    return !hasErrors;
  };

  const handleNext = async (formikProps) => {
    const steps = getSteps(user, organisme, !!amairMembershipStatus);
    const currentStepName = steps[activeStep];
    
    // Vérification spécifique pour l'étape "Informations AMAIR"
    if (currentStepName === 'Informations AMAIR') {
      const isAdherentSAR = formikProps.values.informationsSpecifiques?.AMAIR?.adherentSAR;
      if (isAdherentSAR) {
        // Utilisateur non connecté qui coche SAR
        if (!user) {
          setStepError('Vous êtes adhérent SAR, merci de vous connecter à votre espace adhérent pour continuer votre adhésion à l\'AMAIR.');
          window.scrollTo({ top: 0, behavior: 'smooth' });
          return;
        }
        // Utilisateur connecté qui coche SAR mais n'a pas d'adhésion SAR pour cette année
        if (user && !sarMembershipStatus) {
          setStepError(`Vous n'avez pas d'adhésion SAR pour ${annee}. Veuillez d'abord créer votre adhésion SAR ou décocher cette option.`);
          window.scrollTo({ top: 0, behavior: 'smooth' });
          return;
        }
      }
    }
    
    const isValid = await validateCurrentStep(formikProps, currentStepName);
    
    if (isValid) {
      // Si on est sur l'étape "Informations personnelles" et que l'utilisateur n'est pas connecté
      // Vérifier si l'email existe déjà
      if (currentStepName === 'Informations personnelles' && !user) {
        try {
          const email = formikProps.values.informationsPersonnelles.email;
          const response = await axios.post(`${process.env.REACT_APP_API_URL}/auth/check-email`, { email });
          
          if (response.data.exists) {
            setEmailExistsError(true);
            window.scrollTo({ top: 0, behavior: 'smooth' });
            return;
          }
        } catch (error) {
          console.error('Erreur lors de la vérification de l\'email:', error);
        }
      }
      
      setStepError('');
      setEmailExistsError(false);
      setActiveStep((prevStep) => prevStep + 1);
      // Smooth scroll vers le haut de la nouvelle étape
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } else {
      // Afficher un message d'erreur spécifique selon l'étape
      if (currentStepName === 'Paiement') {
        // Vérifier si c'est une adhésion AMAIR gratuite (adhérent SAR)
        const isAMAIRAdherentSAR = organisme === 'AMAIR' && formikProps.values.informationsSpecifiques?.AMAIR?.adherentSAR;
        if (!isAMAIRAdherentSAR) {
          setStepError('Veuillez sélectionner un mode de paiement pour continuer.');
        } else {
          // Si adhérent SAR, pas besoin de mode de paiement, on peut passer à l'étape suivante
          setStepError('');
          setActiveStep((prevStep) => prevStep + 1);
          window.scrollTo({ top: 0, behavior: 'smooth' });
          return;
        }
      } else if (currentStepName === 'Récapitulatif') {
        setStepError('Veuillez signer électroniquement votre demande d\'adhésion avant de soumettre.');
      } else {
        setStepError('Veuillez remplir tous les champs obligatoires avant de continuer.');
      }
      
      // Faire défiler vers le haut pour voir le message d'erreur
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handleBack = () => {
    setStepError('');
    setEmailExistsError(false);
    setActiveStep((prevStep) => prevStep - 1);
    // Smooth scroll vers le haut de l'étape précédente
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleLoginRedirect = () => {
    navigate('/login');
  };

  const handleSubmit = async (values) => {
    console.log('=== DÉBUT DE LA SOUMISSION (Dedicated) ===');
    console.log('handleSubmit appelé avec values:', values);
    
    try {
      setSubmitError('');
      setIsSubmitting(true);
      
      // Si l'utilisateur n'est pas connecté, créer un compte
      let authToken;
      if (!user) {
        const registerResponse = await axios.post(`${process.env.REACT_APP_API_URL}/auth/register`, {
          type: values.informationsPersonnelles.type,
          raisonSociale: values.informationsPersonnelles.raisonSociale,
          designation: values.informationsPersonnelles.designation,
          email: values.informationsPersonnelles.email,
          password: values.auth.password,
          prenom: values.informationsPersonnelles.prenom,
          nom: values.informationsPersonnelles.nom,
          telephone: values.informationsPersonnelles.telephone,
          telephoneMobile: values.informationsPersonnelles.telephoneMobile,
          adresse: values.informationsPersonnelles.adresse,
          dateNaissance: values.informationsPersonnelles.dateNaissance
        });
        
        // Stocker le token
        authToken = registerResponse.data.token;
        localStorage.setItem('token', authToken);
        
        // Mettre à jour axios defaults pour les requêtes suivantes
        axios.defaults.headers.common['Authorization'] = `Bearer ${authToken}`;
        
        // Mettre à jour le contexte d'authentification
        await login(values.informationsPersonnelles.email, values.auth.password);
      } else {
        authToken = localStorage.getItem('token');
      }

      // Envoyer la demande d'adhésion
      console.log('Envoi de l\'adhésion avec les données:', values);
      console.log('Token utilisé:', authToken ? 'présent' : 'absent');
      console.log('Organisme:', values.organisme);
      
      // Préparer les données pour le backend (enlever les champs non utilisés)
      const adhesionData = {
        organisme: values.organisme,
        annee: values.annee,
        informationsApicoles: values.informationsApicoles,
        informationsPersonnelles: values.informationsPersonnelles,
        informationsSpecifiques: values.informationsSpecifiques,
        adhesionAMAIRGratuite: values.adhesionAMAIRGratuite,
        paiement: values.paiement,
        signature: values.signature,
        // Inclure les documents uploadés
        documents: [
          ...(values.documentsCommuns || []),
          ...(values.documentsSAR || []),
          ...(values.documentsAMAIR || [])
        ]
      };
      
      console.log('Données nettoyées envoyées au backend:', adhesionData);
      console.log('📄 Type de documents dans adhesionData:', typeof adhesionData.documents);
      console.log('📄 Documents est un array:', Array.isArray(adhesionData.documents));
      if (Array.isArray(adhesionData.documents)) {
        adhesionData.documents.forEach((doc, index) => {
          console.log(`📄 Frontend - Document ${index} type:`, typeof doc);
        });
      }
      
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/adhesions`,
        adhesionData,
        {
          headers: {
            Authorization: `Bearer ${authToken}`,
            'Content-Type': 'application/json',
          },
        }
      );
      
      console.log('Adhésion créée avec succès:', response.data);
      
      if (response.data) {
        // Rediriger vers le dashboard après la création de l'adhésion
        // L'utilisateur recevra un email avec le lien de paiement une fois que l'admin aura validé
        console.log('Adhésion créée, navigation vers /dashboard...');
        navigate('/dashboard');
      } else {
        console.error('ERREUR: response.data est vide ou undefined');
      }
    } catch (error) {
      console.error('=== ERREUR LORS DE LA SOUMISSION ===');
      console.error('Erreur complète:', error);
      console.error('Détails de l\'erreur:', error.response?.data);
      console.error('Status:', error.response?.status);
      setSubmitError(
        error.response?.data?.message ||
        'Une erreur est survenue lors de la validation de votre adhésion. Merci de vérifier les informations et de réessayer.'
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderStepContent = (step, formikProps) => {
    const steps = getSteps(user, organisme, !!amairMembershipStatus);
    const stepName = steps[step];

    switch (stepName) {
      case 'Informations personnelles':
        return <InformationsPersonnelles formikProps={formikProps} user={userPersonalInfo} />;
      case 'Informations apicoles':
        return <InformationsApicoles formikProps={formikProps} />;
      case 'Informations AMAIR':
        return <InformationsAMAIR formikProps={formikProps} sarMembershipStatus={sarMembershipStatus} annee={annee} />;
      case 'Adhésion à l\'AMAIR':
        return <AdhesionAMAIRGratuite formikProps={formikProps} />;
      case 'Paiement':
        return <Paiement formikProps={formikProps} />;
      case 'Création de compte':
        return <Authentication formikProps={formikProps} />;
      case 'Récapitulatif':
        return <Recapitulatif formikProps={formikProps} />;
      default:
        return null;
    }
  };

  // Pré-remplir les valeurs initiales si l'utilisateur est connecté et a des infos personnelles
  const getInitialValues = () => {
    const baseValues = getInitialValuesForOrganisme(organisme, annee);
    
    if (user && userPersonalInfo) {
      return {
        ...baseValues,
        informationsPersonnelles: {
          typePersonne: userPersonalInfo.typePersonne || 'personne_physique',
          designation: userPersonalInfo.designation || '',
          raisonSociale: userPersonalInfo.raisonSociale || '',
          nom: userPersonalInfo.nom || '',
          prenom: userPersonalInfo.prenom || '',
          dateNaissance: userPersonalInfo.dateNaissance ? new Date(userPersonalInfo.dateNaissance) : null,
          adresse: {
            rue: userPersonalInfo.adresse?.rue || '',
            codePostal: userPersonalInfo.adresse?.codePostal || '',
            ville: userPersonalInfo.adresse?.ville || ''
          },
          telephone: userPersonalInfo.telephone || '',
          telephoneMobile: userPersonalInfo.telephoneMobile || '',
          email: userPersonalInfo.email || ''
        },
        informationsApicoles: {
          ...baseValues.informationsApicoles,
          napi: userPersonalInfo.napi || '',
          numeroAmexa: userPersonalInfo.numeroAmexa || '',
          siret: userPersonalInfo.siret || ''
        },
        // Pré-cocher "Je suis adhérent SAR" si l'utilisateur a une adhésion SAR pour cette année
        informationsSpecifiques: {
          ...baseValues.informationsSpecifiques,
          AMAIR: {
            ...baseValues.informationsSpecifiques.AMAIR,
            adherentSAR: !!sarMembershipStatus
          }
        }
      };
    }
    return baseValues;
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="text-lg text-gray-600">Chargement...</div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto px-4 pb-8">
      <div className="mb-8">
        <div className="flex items-center gap-4 justify-center">
          {getSteps(user, organisme, !!amairMembershipStatus).map((label, index) => (
            <div key={label} className="flex items-center">
              <div className={`flex items-center justify-center w-8 h-8 rounded-full border-2 
                ${index <= activeStep ? 'border-indigo-600 bg-indigo-600 text-white' : 'border-gray-300 text-gray-300'}`}>
                {index + 1}
              </div>
              {/* {index < getSteps(user, organisme, !!amairMembershipStatus).length - 1 && (
                <div className={`w-full h-1 mx-2 ${index < activeStep ? 'bg-indigo-600' : 'bg-gray-300'}`} />
              )} */}
            </div>
          ))}
        </div>
        {/* <div className="mt-12">
          <h2 className="text-center text-xl font-semilbold">{getSteps(user, organisme, !!amairMembershipStatus)[activeStep]}</h2>
        </div> */}
      </div>

      <Formik
        initialValues={getInitialValues()}
        validationSchema={validationSchema(user, organisme)}
        onSubmit={handleSubmit}
        enableReinitialize
      >
        {(formikProps) => {
          const steps = getSteps(user, organisme, !!amairMembershipStatus);
          const isLastStep = activeStep === steps.length - 1;

          const hasValidationErrors =
            Object.keys(formikProps.errors || {}).length > 0 &&
            formikProps.submitCount > 0;

          return (
            <Form>
              {renderStepContent(activeStep, formikProps)}

              {/* Notification email existant */}
              {emailExistsError && (
                <div className="mt-4 rounded-md bg-honey-50 p-6 border border-honey-200">
                  <div className="flex">
                    <div className="flex-shrink-0">
                      <svg className="h-6 w-6 text-honey-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <div className="ml-3 flex-1">
                      <h3 className="text-sm font-medium text-honey-800">
                        Compte existant
                      </h3>
                      <div className="mt-2 text-sm text-honey-700">
                        <p>
                          Un compte associé à cette adresse email existe déjà. Veuillez vous connecter pour continuer votre adhésion.
                        </p>
                      </div>
                      <div className="mt-4">
                        <button
                          type="button"
                          onClick={handleLoginRedirect}
                          className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                        >
                          Se connecter
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Erreur de validation d'étape */}
              {stepError && (
                <div className="mt-4 rounded-md bg-red-50 p-4 border border-red-200">
                  <div className="flex">
                    <div className="flex-shrink-0">
                      <svg className="h-5 w-5 text-red-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <div className="ml-3">
                      <p className="text-sm text-red-700 font-medium">{stepError}</p>
                    </div>
                  </div>
                </div>
              )}

              {/* Feedback global de validation sur la dernière étape */}
              {isLastStep && hasValidationErrors && (
                <div className="mt-4 rounded-md bg-yellow-50 p-4">
                  <p className="text-sm text-yellow-800">
                    Certaines informations sont manquantes ou invalides. Merci de vérifier les différentes étapes (champs en rouge) avant de soumettre votre adhésion.
                  </p>
                </div>
              )}

              {/* Erreur de soumission côté serveur */}
              {submitError && (
                <div className="mt-4 rounded-md bg-red-50 p-4">
                  <p className="text-sm text-red-700">{submitError}</p>
                </div>
              )}

              <div className="mt-8 flex justify-between">
                {activeStep > 0 && (
                  <button
                    type="button"
                    onClick={handleBack}
                    className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 hover:bg-gray-50"
                  >
                    Précédent
                  </button>
                )}
                <div className="flex-1" />
                {activeStep < steps.length - 1 ? (
                  <button
                    type="button"
                    onClick={() => handleNext(formikProps)}
                    className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700"
                  >
                    Suivant
                  </button>
                ) : (
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50"
                  >
                    {isSubmitting ? 'Envoi en cours...' : 'Soumettre'}
                  </button>
                )}
              </div>
            </Form>
          );
        }}
      </Formik>
    </div>
  );
}
